// ============================================================
// SignUp.jsx
//
// Allows new users to self-register with email and password.
// After signup, they are directed to their User Dashboard.
// They cannot access course-hub until admin grants access.
//
// Google signup is intentionally removed — admin controls
// course-hub access and Google accounts bypass the application
// process. Email/password only for self-registration.
//
// UPDATED (this pass) — closes the access-control wiring gap flagged
// since CourseHubDashboard.jsx started gating by track:
//
//   - This form now reads `track`, `plan`, `supportLevel`, and
//     `allAccess` from the URL query string and writes them to the
//     new user's Firestore doc as `enrolledTracks` / `supportLevel` /
//     `allAccess` — the exact three fields AuthHelpers.getUserAccess()
//     reads and courseAccess.js gates on. Before this change nothing
//     wrote these fields at all, so every new signup fell back to
//     zero access (shared/fail-open courses only) no matter what the
//     student picked on the AI advisor or Pricing page.
//   - AiCourseAssistancePage.jsx already sends ?track=&title= here.
//   - PricingPage.jsx's "Get Started" now sends
//     ?plan=&supportLevel=&track=&allAccess= straight here too, so both
//     entry points (advisor and pricing) land on the same form.
//   - If no query params are present at all (direct visit to
//     /signup), the form now asks for track + plan directly instead
//     of silently defaulting to zero access.
//
// OPEN QUESTION flagged back to Hazzan, NOT resolved here: this file
// also still writes `courseAccess: false`, a SEPARATE, older, manual
// gate — an admin has to flip it to true by hand before the account
// can enter course-hub AT ALL, independent of track/plan. That now
// overlaps with the automatic per-track system above: a Self-Study
// signup (meant to need zero hands-on involvement) still can't get in
// without a manual admin step first. Left this untouched since it's a
// real workflow decision, not a bug — but it works against the
// "make payment plans easy, not much demand from my side" goal this
// whole redesign started from. Needs a decision: retire it now that
// per-track access is automatic, keep it only for the Guided tiers,
// or leave it as-is for everyone.
// ============================================================

import React, { useState } from "react";
import {
    Grid, Card, Container, Typography, TextField,
    Button, CircularProgress, IconButton, InputAdornment,
    Box, styled, MenuItem, Chip,
} from "@mui/material";

import EmailIcon from "@mui/icons-material/Email";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { auth, db } from "../firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useNotification } from "./NotificationProvider";
import { TRACKS } from "../utils/courseCatalog";

// ============================================================
//  CONSTANTS
// ============================================================

const BG_IMAGE = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

// Track options come from the shared catalog (utils/courseCatalog.js), the
// same one the AI advisor, pricing page and Course Hub read — so a track
// added there appears here with no edit. Import path: this file already
// imports "../firebase", so it sits one folder below src/ and
// "../utils/..." reaches src/utils/.
const TRACK_OPTIONS = TRACKS.map((t) => ({ id: t.id, label: t.title }));

// Support-tier ids — keep in sync with PricingPage.jsx's PLANS.
// Enterprise is deliberately not listed: that plan is contact-sales
// only and its CTA never routes through this self-service form.
const SUPPORT_TIER_OPTIONS = [
    { id: "self-study", label: "Self-Study", price: "$49/month" },
    { id: "partially-guided", label: "Partially Guided", price: "$99/month" },
    { id: "fully-guided", label: "Fully Guided", price: "$149/month" },
];

// ============================================================
//  STYLED COMPONENTS  (unchanged)
// ============================================================

const PageWrapper = styled(Grid)(() => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(255,255,255,0.88), rgba(255,255,255,0.88)), url(${BG_IMAGE})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px 20px",
}));

const AuthCard = styled(Card)(() => ({
    width: "100%",
    maxWidth: 520,
    padding: "44px 40px",
    borderRadius: "20px",
    backgroundColor: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(0,0,0,0.08)",
    borderTop: "5px solid goldenrod",
    boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
}));

// ============================================================
//  HELPER
// ============================================================

/**
 * validatePassword
 * Enforces minimum password strength before hitting Firebase.
 */
const validatePassword = (password) => {
    if (password.length < 8) return "Password must be at least 8 characters.";
    if (!/[A-Z]/.test(password)) return "Password must contain at least one uppercase letter.";
    if (!/[0-9]/.test(password)) return "Password must contain at least one number.";
    return "";
};

// ============================================================
//  MAIN COMPONENT
// ============================================================

const SignUp = () => {
    const { notify } = useNotification();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // ── Incoming query params ───────────────────────────────
    // From AiCourseAssistancePage.jsx:            ?track=&title=
    // From PricingPage.jsx:
    //                                   ?plan=&supportLevel=&track=&allAccess=
    const queryTrack = searchParams.get("track");
    const queryTitle = searchParams.get("title");
    const querySupportLevel = searchParams.get("supportLevel") || searchParams.get("plan");
    const queryAllAccess = searchParams.get("allAccess") === "true";

    const validQueryTrack = TRACK_OPTIONS.some((t) => t.id === queryTrack) ? queryTrack : "";
    const validQuerySupportLevel = SUPPORT_TIER_OPTIONS.some((s) => s.id === querySupportLevel) ? querySupportLevel : "";

    // ── Form State ──────────────────────────────────────────
    const [email, setEmail] = useState("");
    const [username, setUsername] = useState("");
    const [track, setTrack] = useState(validQueryTrack);
    const [supportLevel, setSupportLevel] = useState(validQuerySupportLevel);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);

    const trackLabel = TRACK_OPTIONS.find((t) => t.id === track)?.label;
    const tier = SUPPORT_TIER_OPTIONS.find((s) => s.id === supportLevel);

    // ============================================================
    //  HANDLER
    // ============================================================

    /**
     * handleSignUp
     * Creates the Firebase Auth account, then saves a profile to
     * Firestore that carries BOTH:
     *   - the automatic per-track gating fields (enrolledTracks,
     *     supportLevel, allAccess) that courseAccess.js reads, and
     *   - the existing manual courseAccess:false admin gate.
     * Redirects to the User Dashboard either way.
     */
    const handleSignUp = async (e) => {
        e.preventDefault();

        // ── Validation ───────────────────────────────────────
        if (password !== confirmPassword) {
            notify("Passwords do not match.", "error");
            return;
        }
        const pwError = validatePassword(password);
        if (pwError) {
            notify(pwError, "error");
            return;
        }
        if (!track) {
            notify("Please select a track to continue.", "error");
            return;
        }
        if (!supportLevel) {
            notify("Please select a plan to continue.", "error");
            return;
        }

        try {
            setLoading(true);

            // Step 1: Create Firebase Auth account
            const userCredential = await createUserWithEmailAndPassword(
                auth, email, password
            );

            // Step 2: Save profile to Firestore
            await setDoc(doc(db, "users", userCredential.user.uid), {
                email,
                username,
                role: "user",

                // Read by AuthHelpers.getUserAccess() / courseAccess.js
                // to decide which courses show in the Course Hub.
                enrolledTracks: [track],
                supportLevel,
                allAccess: queryAllAccess,

                // Kept for backward compatibility with anything still
                // reading a human-readable "plan" string elsewhere
                // (e.g. an admin view) — derived from the new tier
                // names, not the old Basic/Standard/Premium set.
                plan: tier?.label || supportLevel,

                // STILL a separate, manual gate — see header comment.
                // Admin has to flip this to true before course-hub is
                // reachable at all, on top of the per-track gating
                // above. Not changed this pass.
                courseAccess: false,

                createdBy: "self",
                createdAt: new Date().toISOString(),
                profileComplete: false,
            });

            notify("Account created! Complete your profile to get started.", "success");
            navigate("/user-dashboard");

        } catch (error) {
            notify(error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    //  RENDER
    // ============================================================

    return (
        <PageWrapper container>
            <Container maxWidth="sm">
                <AuthCard>

                    {/* Header */}
                    <Box mb={4}>
                        <Typography
                            variant="h4"
                            fontWeight={800}
                            color="#09122C"
                            gutterBottom
                            sx={{ fontFamily: "'Georgia', serif", letterSpacing: "-0.5px" }}
                        >
                            Create Account
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {queryTitle ? `${queryTitle} track selected. ` : ""}
                            Sign up to access your personal dashboard. Course access
                            is granted by your administrator after payment confirmation.
                        </Typography>
                    </Box>

                    <form onSubmit={handleSignUp}>

                        {/* Email */}
                        <TextField
                            fullWidth label="Email Address" type="email"
                            value={email} onChange={(e) => setEmail(e.target.value)}
                            required
                            InputProps={{ startAdornment: <EmailIcon sx={{ mr: 1, color: "goldenrod" }} /> }}
                            sx={{ mb: 2 }}
                        />

                        {/* Username */}
                        <TextField
                            fullWidth label="Username"
                            value={username} onChange={(e) => setUsername(e.target.value)}
                            required
                            InputProps={{ startAdornment: <PersonIcon sx={{ mr: 1, color: "goldenrod" }} /> }}
                            sx={{ mb: 2 }}
                        />

                        {/* Track — prefilled when they arrived via the AI advisor
                            or Pricing); asked for directly otherwise. */}
                        {validQueryTrack ? (
                            <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Chip
                                    icon={<CheckCircleIcon />}
                                    label={`Track: ${trackLabel}`}
                                    sx={{ bgcolor: "rgba(218,165,32,0.12)", color: "#09122C", fontWeight: 700 }}
                                />
                                <Link to="/ai-course-advisor" style={{ fontSize: "0.8rem", color: "#09122C" }}>
                                    Change
                                </Link>
                            </Box>
                        ) : (
                            <TextField
                                select fullWidth label="Choose Your Track"
                                value={track} onChange={(e) => setTrack(e.target.value)}
                                required sx={{ mb: 2 }}
                            >
                                {TRACK_OPTIONS.map((t) => (
                                    <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
                                ))}
                            </TextField>
                        )}

                        {/* Support plan — prefilled when they arrived via Pricing
                            with a recognized plan; asked for directly otherwise. */}
                        {validQuerySupportLevel ? (
                            <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Chip
                                    icon={<CheckCircleIcon />}
                                    label={`Plan: ${tier?.label} (${tier?.price})`}
                                    sx={{ bgcolor: "rgba(218,165,32,0.12)", color: "#09122C", fontWeight: 700 }}
                                />
                                <Link to="/pricing" style={{ fontSize: "0.8rem", color: "#09122C" }}>
                                    Change
                                </Link>
                            </Box>
                        ) : (
                            <TextField
                                select fullWidth label="Select Your Plan"
                                value={supportLevel} onChange={(e) => setSupportLevel(e.target.value)}
                                required sx={{ mb: 2 }}
                            >
                                {SUPPORT_TIER_OPTIONS.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>{s.label} — {s.price}</MenuItem>
                                ))}
                            </TextField>
                        )}

                        {queryAllAccess && (
                            <Chip
                                label="All-Access — every track included"
                                size="small"
                                sx={{ mb: 2, bgcolor: "rgba(218,165,32,0.12)", color: "#09122C", fontWeight: 700 }}
                            />
                        )}

                        {/* Password */}
                        <TextField
                            fullWidth label="Password"
                            type={showPassword ? "text" : "password"}
                            value={password} onChange={(e) => setPassword(e.target.value)}
                            required
                            helperText="Min 8 characters, one uppercase, one number"
                            InputProps={{
                                startAdornment: <LockOpenIcon sx={{ mr: 1, color: "goldenrod" }} />,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2 }}
                        />

                        {/* Confirm Password */}
                        <TextField
                            fullWidth label="Confirm Password"
                            type={showConfirm ? "text" : "password"}
                            value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            InputProps={{
                                startAdornment: <LockOpenIcon sx={{ mr: 1, color: "goldenrod" }} />,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                                            {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 3 }}
                        />

                        {/* Submit */}
                        <Button
                            fullWidth type="submit" variant="contained"
                            disabled={loading}
                            sx={{
                                backgroundColor: "#09122C", color: "goldenrod",
                                fontWeight: "bold", py: 1.4, fontSize: "1rem",
                                "&:hover": { backgroundColor: "#142850" }, mb: 2,
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : "Create Account"}
                        </Button>

                        {/* Sign In Link */}
                        <Typography variant="body2" textAlign="center" color="text.secondary">
                            Already have an account?{" "}
                            <Link to="/signin" style={{ color: "#09122C", fontWeight: "bold" }}>
                                Sign In
                            </Link>
                        </Typography>

                    </form>
                </AuthCard>
            </Container>
        </PageWrapper>
    );
};

export default SignUp;
